import React, { useEffect, useRef, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Button } from "antd";
import { HomeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { NavLink, useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import {
  ActionType,
  RequestData,
  RequestOptionsType,
} from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import {
  SurveysGetParams,
  SurveyStatus,
  SurveyStatusColor,
} from "../../../hooks/api/surveys";
import { getSorterParams } from "../../../utils";
import { dateTimeFormat } from "../../../contstant";
import {
  ResponsesTableRow,
  useResponsesTableGet,
} from "../../../hooks/api/responses";
import { LazySelect } from "./Filters";
import DownloadButton from "./actions";

export function getSurveyStatusColor(status: SurveyStatus): string {
  return SurveyStatusColor[status] || "rgba(0,0,0,.15)";
}

function ToolBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [
    // <Button
    //   key="add"
    //   icon={<PlusCircleOutlined />}
    //   type="primary"
    //   onClick={() => navigate("create")}
    // >
    //   Add
    // </Button>,
  ];
}

let userCache: RequestOptionsType[] | null = null;

export default function ResponsesContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  // const templatesGet = useTemplatesTableGet();
  const responsesTableGet = useResponsesTableGet();
  const actionRef = useRef<ActionType>(null);

  const loadUserOptions = async (
    reviewTableGet: ReturnType<typeof useResponsesTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (userCache) return userCache;

    const response = await reviewTableGet.fetch({
      page: 1,
      pageSize: 100,
    });

    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();

    response.data.forEach((item) => {
      const user = item.user;
      if (user?.id) {
        map.set(user.id, {
          label: `${user.firstName} ${user.lastName}`,
          value: user.id,
        });
      }
    });

    userCache = Array.from(map.values());

    return userCache;
  };
  const loadOrderOptions = async (
    reviewTableGet: ReturnType<typeof useResponsesTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (userCache) return userCache;

    const response = await reviewTableGet.fetch({
      page: 1,
      pageSize: 100,
    });

    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();

    response.data.forEach((item) => {
      const user = item.user;
      if (user?.id) {
        map.set(user.id, {
          label: `${user.firstName} ${user.lastName}`,
          value: user.id,
        });
      }
    });

    userCache = Array.from(map.values());

    return userCache;
  };

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Responses" },
    ]);
  }, [i18n.language, t]);

  const columns = (t: TFunction): ProColumns<ResponsesTableRow>[] => [
    {
      title: "Order ID",
      dataIndex: "jobId",
      key: "jobId",
      valueType: "select",
      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await responsesTableGet.fetch({
              page,
              pageSize,
              search,
            });

            return res?.data || [];
          }}
          mapOption={(item) => ({
            label: item.jobId,
            value: item.jobId,
          })}
        />
      ),
      search: {
        transform: (value) => ({
          jobId: value,
        }),
      },
    },
    {
      title: "Survey title",
      dataIndex: "title",
      key: "title",
      hideInSearch: true,
      ellipsis: true,
    },
    {
      title: "Job",
      dataIndex: "job",
      key: "job",
      hideInSearch: true,
      hideInTable: true,
    },
    {
      title: "Field technician",
      dataIndex: "userName",
      key: "userName",
      sorter: true,
      hideInSearch: true,
      render: (text, record) => (
        <NavLink
          to={`/users/${record.user?.id}/edit`}
          style={{ textDecoration: "underline" }}
        >
          {record.user?.firstName} {record.user?.lastName}
        </NavLink>
      ),
    },
    {
      title: "Submitted at",
      dataIndex: "submittedDate",
      key: "submittedDate",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        if (!record.submittedDate) return "-";

        return dayjs
          .utc(record.submittedDate)
          .add(Number(record.submittedTimezone || 0), "hour")
          .format(dateTimeFormat);
      },
      ellipsis: true,
    },
    {
      title: "Submitted at",
      dataIndex: "submittedDate",
      key: "submittedDate",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
    {
      title: "Field technician",
      dataIndex: "userName",
      key: "userName",
      valueType: "select",
      hideInTable: true,

      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await responsesTableGet.fetch({
              page,
              pageSize,
              search,
            });
            return (res?.data || []).filter((item) => item.user?.id);
          }}
          mapOption={(item) => ({
            label: [item.user?.firstName, item.user?.lastName]
              .filter(Boolean)
              .join(" "),
            value: item.user!.id,
          })}
        />
      ),

      search: {
        transform: (value) => ({
          userId: value,
        }),
      },

      render: (_, record) => (
        <div>
          {record.user?.firstName} {record.user?.lastName}
        </div>
      ),
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, any> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ResponsesTableRow>>> => {
    const newParams: SurveysGetParams = {
      page: current || 1,
      pageSize: pageSize || 10,
      orderByColumn: "updatedAt",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return responsesTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;
        return {
          data: data || [],
          success: true,
          total,
        };
      }
      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <Table<ResponsesTableRow>
      key={`${tableKey}-${i18n.language}`}
      resetKey={() => setTableKey(Date.now())}
      className={clsx("transparent")}
      headerTitle="List of Responses"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns(t)}
      actionRef={actionRef}
      actions={[
        [
          "edit",
          ({ record }) => ({
            title: "Edit Response",
            onClick: (e: MouseEvent) => {
              e.preventDefault();
              const currentPage = actionRef.current?.pageInfo?.current;
              navigate(`${record.id}/edit`, {
                state: { fromPage: currentPage?.toString() },
              });
            },
          }),
        ],
        [
          DownloadButton,
          {
            setTableKey,
          },
        ],
      ]}
      search={{
        style: {
          // display: "none",
        },
      }}
      options={{
        search: {
          name: "search",
          placeholder: "Search",
          style: { width: 296 },
        },
      }}
      columnsState={{
        persistenceKey: "pro-table-templates",
        persistenceType: "localStorage",
      }}
    />
  );
}
